import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../lib/content";
import { FadeUp, MaskReveal } from "./Reveal";

const url = (src) => `${process.env.PUBLIC_URL || ""}/images/${src}`;
const pad = (n) => String(n).padStart(2, "0");

const pageVariants = {
  enter: (dir) => ({ rotateY: dir >= 0 ? 88 : -88, opacity: 0, scale: 0.96 }),
  center: { rotateY: 0, opacity: 1, scale: 1 },
  exit: (dir) => ({ rotateY: dir >= 0 ? -88 : 88, opacity: 0, scale: 0.96 }),
};

export default function Gallery() {
  const [[index, dir], setIndex] = useState([0, 0]);
  const [open, setOpen] = useState(false);
  const n = GALLERY.length;

  const go = useCallback((d) => setIndex(([i]) => [(i + d + n) % n, d]), [n]);
  const jump = (target) => setIndex(([i]) => [target, target > i ? 1 : -1]);

  useEffect(() => {
    if (open) return;
    const t = setInterval(() => go(1), 4200);
    return () => clearInterval(t);
  }, [go, open]);

  const current = GALLERY[index];

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-[var(--bg)] px-6 md:px-12 py-28 md:py-44"
    >
      <div className="mx-auto max-w-[1500px]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
          <div>
            <FadeUp className="mb-6 flex items-center gap-6">
              <span className="h-px w-12 bg-[var(--gold)]" />
              <span className="font-mono text-[11px] tracking-[0.35em] text-[var(--gold-ink)] uppercase">
                Φωτογραφίες / Gallery
              </span>
            </FadeUp>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] text-[var(--fg)]">
              <MaskReveal>Στιγμές από</MaskReveal>
              <MaskReveal delay={0.08} className="italic text-[var(--gold-ink)]">
                τον δρόμο μας.
              </MaskReveal>
            </h2>
          </div>
          <FadeUp delay={0.15}>
            <p className="max-w-xs font-body text-sm text-[var(--muted)] leading-relaxed">
              Ζωντανές εμφανίσεις, λατρεία και παρέα — ξεφύλλισε τις στιγμές μας.
            </p>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Book stage */}
          <FadeUp y={50} className="lg:col-span-8">
            <div style={{ perspective: 2200 }}>
              <div className="relative aspect-[16/10] w-full overflow-hidden rounded-sm ring-1 ring-[var(--line-gold)] bg-[var(--surface)]">
                <AnimatePresence custom={dir} initial={false} mode="popLayout">
                  <motion.img
                    key={current.src}
                    data-testid="book-image"
                    custom={dir}
                    variants={pageVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
                    src={url(current.src)}
                    alt={current.caption}
                    onClick={() => setOpen(true)}
                    style={{ transformOrigin: "left center", backfaceVisibility: "hidden" }}
                    className="absolute inset-0 h-full w-full object-cover cursor-pointer"
                  />
                </AnimatePresence>

                {/* book spine + page shading */}
                <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-black/50 via-black/15 to-transparent" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#0a0a0c]/85 to-transparent" />
                <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/5" />

                <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between">
                  <span className="font-serif text-xl md:text-2xl text-white drop-shadow-[0_1px_8px_rgba(10,10,12,0.7)]">
                    {current.caption}
                  </span>
                  <span className="font-mono text-[11px] tracking-[0.25em] text-[var(--gold)]">
                    {pad(index + 1)} / {pad(n)}
                  </span>
                </div>
              </div>

              {/* controls */}
              <div className="mt-6 flex items-center gap-4">
                <button
                  data-testid="book-prev"
                  onClick={() => go(-1)}
                  aria-label="Προηγούμενη"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--line)] text-[var(--fg)] hover:border-[var(--gold)] hover:text-[var(--gold-ink)] transition-colors duration-300"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  data-testid="book-next"
                  onClick={() => go(1)}
                  aria-label="Επόμενη"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--line)] text-[var(--fg)] hover:border-[var(--gold)] hover:text-[var(--gold-ink)] transition-colors duration-300"
                >
                  <ChevronRight size={18} />
                </button>
                <div className="ml-2 flex gap-2">
                  {GALLERY.map((_, i) => (
                    <button
                      key={i}
                      data-testid={`book-dot-${i}`}
                      onClick={() => jump(i)}
                      aria-label={`Φωτογραφία ${i + 1}`}
                      className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${
                        i === index ? "w-8 bg-[var(--gold)]" : "w-3 bg-[var(--fg)]/25 hover:bg-[var(--fg)]/50"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </FadeUp>

          {/* Thumbnail rail */}
          <FadeUp delay={0.1} className="lg:col-span-4">
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--muted)] mb-5">
              Άλμπουμ · {pad(n)} στιγμές
            </p>
            <div className="grid grid-cols-3 lg:grid-cols-2 gap-3">
              {GALLERY.map((g, i) => (
                <button
                  key={g.src}
                  data-testid={`book-thumb-${i}`}
                  onClick={() => jump(i)}
                  className={`group relative aspect-[4/3] overflow-hidden rounded-sm ring-1 transition-[ring-color] duration-300 ${
                    i === index ? "ring-[var(--gold)]" : "ring-[var(--line)] hover:ring-[var(--gold-soft)]"
                  }`}
                >
                  <img
                    src={url(g.src)}
                    alt={g.caption}
                    loading="lazy"
                    className={`h-full w-full object-cover transition-[filter,transform] duration-500 group-hover:scale-105 ${
                      i === index ? "brightness-110" : "brightness-[0.6] group-hover:brightness-90"
                    }`}
                  />
                  {i === index && (
                    <span className="absolute inset-0 ring-2 ring-inset ring-[var(--gold)]/40" />
                  )}
                </button>
              ))}
            </div>
          </FadeUp>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[95] flex items-center justify-center bg-[var(--bg)]/95 backdrop-blur-md p-4 md:p-12"
          >
            <button
              data-testid="lightbox-close"
              className="absolute top-6 right-6 text-white hover:text-[var(--gold)] transition-colors"
              onClick={() => setOpen(false)}
              aria-label="Κλείσιμο"
            >
              <X size={28} />
            </button>
            <motion.figure
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl max-h-[85vh] flex flex-col items-center"
            >
              <img
                src={url(current.src)}
                alt={current.caption}
                className="max-h-[78vh] w-auto object-contain rounded-sm ring-1 ring-[var(--line-gold)]"
              />
              <figcaption className="mt-4 font-mono text-[11px] tracking-[0.3em] uppercase text-white/60">
                {current.caption}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

